import React, { useState, useMemo, useEffect, useCallback } from "react";
import DotPagination from "./DotPagination";
import { dashboardColors, theme } from "../../../../themes/Theme";
import LoadingSpinner from "../../../../revampComponents/Atom/Loading";
import { EVENT_STATUS, formatCurrency, formatEventDateTime } from "../AccountLevelReportConstant";
import Loading from "../../../../components/Loading";
import { cutString } from "../../../../services/util";
import redirectIcon from "../../../../assets/img/reports/open-in-new-tab.svg";
import { getISTDateNTime } from "../../../../Utils/helpers";

const CARDS_PER_PAGE = 6;

export default function EventCard({
  events = [],
  onEventClick,
  loading = false,
  eventStatus = "active",
  dateFilter = "lastWeek",
  selectedCurrency= null,
  totalCount = 0,
  onCardPaginationChange
}) {
  const [page, setPage] = useState(1);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);

    if (onCardPaginationChange) {
      const offset = (newPage - 1) * CARDS_PER_PAGE;
      const paginationData = {
        page: newPage,
        limit: CARDS_PER_PAGE,
        offset: offset,
        eventStatus: eventStatus,
        dateFilter: dateFilter
      };

      onCardPaginationChange(paginationData);
    }
  }, [eventStatus, dateFilter, onCardPaginationChange]); 

  const eventsData = useMemo(() => {
    if (Array.isArray(events)) {
      return events;
    }
    if (events?.events_summary && Array.isArray(events?.events_summary)) {
      return events?.events_summary;
    }
    return [];
  }, [events]);

  const pageCount = Math.ceil(totalCount / CARDS_PER_PAGE);
  const startIndex = (page - 1) * CARDS_PER_PAGE;

  const paginated = eventsData?.slice(startIndex, startIndex + CARDS_PER_PAGE);
  const displayEvents = onCardPaginationChange ? eventsData : (() => {
    const startIndex = (page - 1) * CARDS_PER_PAGE;
    return eventsData?.slice(startIndex, startIndex + CARDS_PER_PAGE);
  })();

  useEffect(() => {
    setPage(1);
  }, [eventStatus, dateFilter]);



  if (eventsData?.length === 0 && !loading) {
    return (
      <div className="no-events-container">
        <div className="no-events-content d-flex py-4 my-4 flex-column justify-content-center align-items-center">
          <p className="py-4 my-4">No events found for the selected filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="event-cards-container">
      {loading ? <div className="loading-container">
        	<Loading size="40px" weight={"16px"} color= {dashboardColors[theme].primary}/>
      </div> : 
    <div className="d-flex flex-column">
      <div className="row event-summary-grid-cont">
        {displayEvents?.map((event, index) => (
          <div className="col-12 col-md-4 px-2" key={event?.event_id || index}>
            <div className="card h-100 event-summary-dashboard-card">
              <div className="report-summary-card-body">
                <div className="d-flex align-items-start mb-3 richtext-gap-2">
                  <img
                    alt={event?.name || "Event"}
                    src={event?.event_poster_url}
                    className="event-summary-dashboard-avatar me-2"
                  />
                  <div className="flex-grow-1">
                    <div className="event-summary-event-title" title={event?.name}>
                      {cutString(event?.name, 20)}
                    </div>
                    <div className="d-flex align-items-center mb-1 flex-wrap">
                      <span className="status-container">
                        <span
                          className={`dashboard-dot me-1 ${event?.event_status === EVENT_STATUS.ACTIVE
                              ? 'dashboard-active-dot'
                              : event?.event_status === EVENT_STATUS.UPCOMING
                                ? 'dashboard-upcoming-dot'
                                : 'dashboard-past-dot'
                            }`}
                        ></span>

                        <span className="dashboard-active">
                          {event?.event_status === EVENT_STATUS.ACTIVE
                            ? 'Active'
                            : event?.event_status === EVENT_STATUS.UPCOMING
                              ? 'Upcoming'
                              : 'Past'}
                        </span>

                      </span>
                      <span className="event-summary-event-start-time">
                         {getISTDateNTime(event?.start_date + " " + event?.start_time, event?.time_zone,"needed")} 
                      </span>
                    </div>
                  </div>
                  <button
                    className="event-action-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEventClick && onEventClick(event?.event_id);
                    }}
                    title="Go to Event Dashboard"
                  >
                     <img src={redirectIcon} alt="Go to Event Dashboard" />
                  </button>
                </div>

                <div className="row g-2 event-summary-card-gap">
                  <div className="col-6">
                    <div className="event-summary-dashboard-metric">
                      <div className="event-summary-card-metric-label">Registrations</div>
                      <div className="event-summary-card-metric-value">
                        {event?.registrations_count || 0}
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="event-summary-dashboard-metric">
                      <div className="event-summary-card-metric-label">Sales</div>
                      <div className="event-summary-card-metric-value">
                        {formatCurrency(event?.sale,selectedCurrency?.symbol)}
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="event-summary-dashboard-metric">
                      <div className="event-summary-card-metric-label">Expenses</div>
                      <div className="event-summary-card-metric-value">
                        {formatCurrency(event?.expense,selectedCurrency?.symbol)}
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="event-summary-dashboard-metric">
                      <div className="event-summary-card-metric-label">Income</div>
                      <div className="event-summary-card-metric-value event-summary-text-success">
                        {formatCurrency(event?.income,selectedCurrency?.symbol)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {pageCount > 1 && (
        <div className="pagination-container">
              <DotPagination
                page={page}
                pageCount={pageCount}
                onPageChange={handlePageChange}
              />
        </div>
      )}
</div>}
      <style jsx>{`
        .event-cards-container {
          padding: 0 20px;
        }

        .report-events-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .events-count-info {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }
        
        .report-summary-card-body {
          flex: 1 1 auto;
          padding: 1.25rem 0.75rem;
        }
        
        .loading-container, .no-events-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #6c757d;
          text-align: center;
          min-height: 300px;
        }

        .no-events-content h4 {
          margin-bottom: 12px;
          color: #374151;
        }

        .no-events-content p {
          margin-bottom: 8px;
          font-size: 16px;
        }

        .no-events-content small {
          font-size: 14px;
          color: #9ca3af;
        }

        .event-summary-grid-cont {
          gap: 30px 0;
        }

        .event-summary-dashboard-card {
          border-radius: 4px;
          box-shadow: 0px 0px 14px 9px #002E6E1A;
          border: 0.5px solid #C3CDDF;
          transition: all 0.3s ease;
          height: 100%;
        }

        .event-summary-card-metric-label {
          font-size: 14px;
          font-weight: 400;
          color: #333333;
        }

        .event-summary-event-start-time {
          font-size: 12px;
          font-weight: 400;
          color: #000000;
          margin-left: 8px;
        }

        .event-summary-event-title {
          font-size: 16px;
          font-weight: 500;
          color: #000000;
          line-height: 1.4;
          margin-bottom: 4px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .event-summary-card-metric-value {
          font-size: 16px;
          font-weight: 600;
          color: ${dashboardColors[theme].primaryTextColor};
          margin-top: 4px;
        }

        .event-summary-text-success {
          color: #34A853 !important;
        }

        .event-summary-dashboard-avatar {
          width: 76px;
          height: 38px;
          border-radius: 6px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .dashboard-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }
        .dashboard-past-dot{
        background: #DC3545;
        }
        .dashboard-active-dot{
         background: #FDB631;
           
        }

        .dashboard-active {
          color: #000000;
          font-weight: 400;
          font-size: 12px;
        }


        .status-container {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .event-summary-card-gap{
            row-gap:20px;
        }
        .event-action-btn {
          background: transparent;
          border: none;
          padding: 8px 2px;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
          flex-shrink: 0;
        }

        .event-action-btn:hover {
          background-color: #f3f4f6;
        }

        .event-summary-dashboard-metric {
          padding: 12px;
          border-radius: 4px;
          background: #fff;
          border: 0.5px solid #C3CDDF;
          display: flex;
          flex-direction: column;
          height: 100%;
          transition: border-color 0.2s;
        }

        .event-summary-dashboard-metric:hover {
          border-color: #9ca3af;
        }
        .dashboard-upcoming-dot{
            background-color:#FDB631;
            width: 8px;
            height: 8px;
          }
        .pagination-container {
          display: flex;
          justify-content: center;
          margin-top: 32px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .event-cards-container {
            padding: 0 15px;
          }

          .report-events-info {
            margin-bottom: 15px;
          }

          .event-summary-dashboard-avatar {
            width: 60px;
            height: 30px;
          }
         

          .event-summary-event-title {
            font-size: 13px;
          }

          .event-summary-card-metric-value {
            font-size: 14px;
          }

          .report-summary-card-body {
            padding: 0.75rem;
          }
        }

        @media (max-width: 576px) {
          .event-summary-grid-cont {
            gap: 20px 0;
          }

          .event-summary-dashboard-card {
            margin-bottom: 0;
          }
        }
      `}</style>
    </div>
  );
}
